import { useState } from 'react';

import { usePipelineJobs, useSubmitPipelineJob } from 'src/api/queries';
import { JobQueue } from 'src/components/editorial/JobQueue';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { StagePicker } from 'src/components/editorial/StagePicker';
import { Button } from 'src/components/ui/Button';
import { Skeleton } from 'src/components/ui/Skeleton';
import { MACHINE_COPY, buildJobBoard, formatDuration, type JobView, type MachineState } from 'src/lib/jobBoard';
import { describeSubmitFailure, STAGE_LABEL, stagesUpTo } from 'src/lib/pipelineStages';
import type { PipelineStage } from 'src/types/editorial';

/** How many settled runs the history shows before it asks. */
const HISTORY_SHOWN = 8;

/**
 * The processing console: what the machine is doing, and what to hand it next.
 *
 * The order matters and it inverted. This screen used to open on a form, which is the right shape
 * for the first visit and the wrong one for every other: an editor comes here to find out whether
 * the mañanera they queued forty minutes ago is moving. So the state of the machine and the runs
 * in flight are the primary region, and asking for a new one is a section further down.
 */
export function ProcessingWorkspace() {
  const [sourceUrl, setSourceUrl] = useState('');
  const [stopAfter, setStopAfter] = useState<PipelineStage>('prenota');
  const [queued, setQueued] = useState<{ jobId: string; instanceStarted: boolean } | null>(null);
  const [retrying, setRetrying] = useState('');

  const jobs = usePipelineJobs({ limit: 40 });
  const submit = useSubmitPipelineJob();

  const trimmedUrl = sourceUrl.trim();
  // `dataUpdatedAt` rather than a clock of our own: the board's ages are relative to the answer on
  // screen, and a ticking `Date.now()` would age a snapshot that has not changed.
  const board = buildJobBoard(jobs.data?.jobs ?? [], jobs.dataUpdatedAt || Date.now(), jobs.data?.machine ?? 'unknown');

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setQueued(null);
    submit.mutate(
      { sourceUrl: trimmedUrl, stages: stagesUpTo(stopAfter) },
      {
        onSuccess: result => {
          setQueued(result);
          setSourceUrl('');
        },
      }
    );
  }

  /** Ask for exactly what the failed run asked for. A retry that changes the request is not one. */
  function handleRetry(view: JobView) {
    setRetrying(view.job.jobId);
    setQueued(null);
    submit.mutate(
      {
        ...(view.job.resolvedStreamId || view.job.streamId
          ? { streamId: view.job.resolvedStreamId || view.job.streamId }
          : { sourceUrl: view.job.sourceUrl }),
        stages: view.job.stages,
      },
      {
        onSuccess: result => setQueued(result),
        onSettled: () => setRetrying(''),
      }
    );
  }

  return (
    <div className="page-wide">
      <PageHeader title="Procesamiento">
        La máquina que procesa está apagada casi todo el día para no costar de más. Lo que pidas aquí queda en cola y se
        enciende sola: si tarda unos minutos en arrancar es eso, no un error.
      </PageHeader>

      {jobs.isPending && <Skeleton variant="rows" count={4} />}

      {jobs.isError && (
        <LoadFailure
          title="No se pudo cargar la cola"
          error={jobs.error}
          onRetry={jobs.refetch}
          whenMissing="La cola necesita una versión del API que quizá aún no está desplegada. Si acabas de actualizar el dashboard, despliega envoz-server."
        />
      )}

      {jobs.data && (
        <>
          <MachineBand
            machine={board.machine}
            running={board.running.length}
            queuedCount={board.queued.length}
            throughput={board.throughput}
            polling={jobs.isFetching}
          />

          <section className="console__block">
            <h2 className="section-title">
              En curso <span className="console__count">{board.running.length}</span>
            </h2>
            <JobQueue
              views={board.running}
              emptyNote="Nada corriendo en este momento."
              onRetry={handleRetry}
              retrying={retrying}
            />
          </section>

          <section className="console__block">
            <h2 className="section-title">
              En cola <span className="console__count">{board.queued.length}</span>
            </h2>
            <JobQueue
              views={board.queued}
              emptyNote="La cola está vacía."
              onRetry={handleRetry}
              retrying={retrying}
            />
          </section>
        </>
      )}

      <section className="console__block" id="pedir">
        <h2 className="section-title">Pedir un video</h2>
        <form className="process-form" onSubmit={handleSubmit}>
          <label className="process-form__field">
            <span>Liga del video en YouTube</span>
            <input
              type="url"
              value={sourceUrl}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={event => setSourceUrl(event.target.value)}
              required
            />
          </label>

          <fieldset className="process-form__stages">
            <legend>¿Hasta dónde procesarlo?</legend>
            <p className="process-form__hint">
              Todo lo anterior a la etapa que elijas también corre: cada etapa se alimenta de la anterior. Lo que quede
              fuera se puede pedir después desde la transmisión, sin volver a transcribir.
            </p>
            <StagePicker value={stopAfter} onChange={setStopAfter} disabled={submit.isPending} />
          </fieldset>

          <div className="process-form__actions">
            <Button type="submit" variant="primary" busy={submit.isPending && !retrying} disabled={!trimmedUrl}>
              Procesar hasta {STAGE_LABEL[stopAfter]}
            </Button>
            {submit.isError && <SubmitFailure error={submit.error} />}
            {queued && (
              <p className="voice-actions__ok">
                En cola ({queued.jobId}).{' '}
                {queued.instanceStarted ? 'La máquina está arrancando.' : 'Se procesará en el siguiente ciclo.'}
              </p>
            )}
          </div>
        </form>
      </section>

      {jobs.data && board.history.length > 0 && (
        <section className="console__block">
          <h2 className="section-title">Historial</h2>
          <JobQueue views={board.history.slice(0, HISTORY_SHOWN)} emptyNote="" onRetry={handleRetry} retrying={retrying} />
          {/* The rest stay reachable. A console that opens on forty settled runs buries the two
              that are still moving, which is the only reason anyone opened it. */}
          {board.history.length > HISTORY_SHOWN && (
            <details className="console__more">
              <summary>{board.history.length - HISTORY_SHOWN} corridas anteriores</summary>
              <JobQueue
                views={board.history.slice(HISTORY_SHOWN)}
                emptyNote=""
                onRetry={handleRetry}
                retrying={retrying}
              />
            </details>
          )}
        </section>
      )}

      <section className="quiet-tail">
        <h2>Cómo leer esta pantalla</h2>
        <p>
          Una corrida en cola no está detenida: la máquina se enciende sola y tarda unos minutos. Una corrida en curso
          avanza por etapas y cada etapa se alimenta de la anterior, así que el avance salta de golpe cuando una
          termina, no de forma continua. Los tiempos son relativos al último refresco de esta pantalla, no a tu reloj.
        </p>
      </section>
    </div>
  );
}

/**
 * The state of the box, at the top, where the question actually is.
 *
 * Three states rather than a spinner: "apagada" and "encendiendo" look identical from a queue row
 * and mean opposite things about whether to wait.
 */
function MachineBand({
  machine,
  running,
  queuedCount,
  throughput,
  polling,
}: {
  machine: MachineState;
  running: number;
  queuedCount: number;
  throughput: { finishedToday: number; failedToday: number; medianMs: number | null };
  polling: boolean;
}) {
  const copy = MACHINE_COPY[machine];

  return (
    <section className="machine" data-state={machine}>
      <div className="machine__state">
        <span className="machine__dot" data-tone={copy.tone} aria-hidden="true" />
        <div>
          <p className="machine__label">{copy.label}</p>
          <p className="machine__detail">{copy.detail}</p>
        </div>
      </div>

      <dl className="machine__figures">
        <div>
          <dd>{running}</dd>
          <dt>en curso</dt>
        </div>
        <div>
          <dd>{queuedCount}</dd>
          <dt>en cola</dt>
        </div>
        <div>
          <dd>{throughput.finishedToday}</dd>
          <dt>listas · 24 h</dt>
        </div>
        <div>
          <dd data-tone={throughput.failedToday > 0 ? 'contradicted' : undefined}>{throughput.failedToday}</dd>
          <dt>fallidas · 24 h</dt>
        </div>
        <div>
          {/* Null, not zero: "no runs finished today" and "runs took no time" are different facts. */}
          <dd className="machine__figure-small">{formatDuration(throughput.medianMs)}</dd>
          <dt>mediana por corrida</dt>
        </div>
      </dl>

      {/* The refresh is stated rather than animated — the queue polls itself only while something
          is in flight, and an editor watching a still screen deserves to know which it is. */}
      <p className="machine__poll">{polling ? 'Actualizando…' : machine === 'idle' ? 'Sin refresco automático' : 'Se actualiza solo'}</p>
    </section>
  );
}

/**
 * A refusal, explained on the screen where it happened.
 *
 * The most common one — this video is already queued — is not a failure: the work is already going
 * to happen. It read as a breakage because the raw English sentence from the API was rendered as
 * an error line, which is what an editor reported.
 */
function SubmitFailure({ error }: { error: unknown }) {
  const { headline, detail, isAlreadyQueued } = describeSubmitFailure(error);

  return (
    <p className={isAlreadyQueued ? 'voice-actions__note' : 'voice-actions__error'}>
      <strong>{headline}.</strong> {detail}
    </p>
  );
}
